<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { useLobbyStore, type TableInfo } from '@/stores/lobby'
import TableFormModal from '@/components/lobby/TableFormModal.vue'
import ArmyTokenPreview from '@/components/army/ArmyTokenPreview.vue'
import PlayerProfileModal from '@/components/common/PlayerProfileModal.vue'
import { roleColor } from '@/services/rolePresentation'
import { presentPresence } from '@/services/presencePresentation'
import { normalizeFriends, type FriendItem } from '@/services/socialPresentation'
import { MY_TABLES_EVENT } from '@/services/profilePopup'

interface LogoToken {
  id: string
  name: string
  image_url: string
  kind?: 'h' | 'b' | 'a' | 'u' | 'f' | 'l' | 'm'
  rect?: { x?: number; y?: number; w?: number; h?: number }
}
interface LogoGroup { id: string; name: string; tokens: LogoToken[] }

const auth = useAuthStore()
const lobby = useLobbyStore()
const router = useRouter()
const menuOpen = ref(false)
const popup = ref<'tables' | 'friends' | 'account' | 'logo' | null>(null)
const myTables = ref<TableInfo[]>([])
const friends = ref<FriendItem[]>([])
const selectedFriend = ref<FriendItem | null>(null)
const logoGroups = ref<LogoGroup[]>([])
const selectedLogo = ref<string | null>(null)
const editingTable = ref<TableInfo | null>(null)
const loading = ref(false)
const message = ref('')
const tableLimitNotice = ref(false)
const rootRef = ref<HTMLElement | null>(null)
const leagueFirstName = ref('')
const leagueLastName = ref('')
const savingLeagueData = ref(false)
let friendPresenceTimer: ReturnType<typeof setInterval> | null = null
let friendRefreshInFlight = false

const profileLogo = computed(() => auth.user?.logo_url || '/media/site/icon.avif')
const profileLogoKind = computed(() => auth.user?.logo_kind || 'u')
const profileLogoRect = computed(() => auth.user?.logo_rect || null)
const atTableLimit = computed(() => !auth.isAdmin && myTables.value.length >= 2)
function friendPresence(friend: FriendItem) {
  const user = lobby.onlineUsers.find(item => item.id === friend.id)
  const tableName = user?.current_table
    ? lobby.tables.find(table => table.id === user.current_table)?.name
    : undefined
  return presentPresence(user, tableName)
}

function closeAll(): void {
  stopFriendPresenceRefresh()
  menuOpen.value = false
  popup.value = null
  editingTable.value = null
  selectedFriend.value = null
  message.value = ''
  tableLimitNotice.value = false
}

function stopFriendPresenceRefresh(): void {
  if (friendPresenceTimer !== null) {
    clearInterval(friendPresenceTimer)
    friendPresenceTimer = null
  }
}

async function refreshFriendPresence(): Promise<void> {
  if (popup.value !== 'friends' || friendRefreshInFlight || document.hidden) return
  friendRefreshInFlight = true
  try {
    await lobby.fetchOnlineUsers()
  } catch {
    // Presence is supplementary; keep the already rendered friend list stable.
  } finally {
    friendRefreshInFlight = false
  }
}

function startFriendPresenceRefresh(): void {
  stopFriendPresenceRefresh()
  friendPresenceTimer = setInterval(() => void refreshFriendPresence(), 2000)
}

async function loadFriends(): Promise<void> {
  const [{ data }] = await Promise.all([
    api.get<FriendItem[]>('/social/friends'),
    lobby.fetchOnlineUsers(),
  ])
  friends.value = normalizeFriends(data)
}

function onDocumentPointerDown(event: PointerEvent): void {
  if (!menuOpen.value) return
  const target = event.target as Node | null
  if (target && rootRef.value?.contains(target)) return
  menuOpen.value = false
}

async function openPopup(kind: typeof popup.value): Promise<void> {
  if (kind !== 'friends') stopFriendPresenceRefresh()
  menuOpen.value = false
  popup.value = kind
  tableLimitNotice.value = false
  message.value = ''
  loading.value = true
  try {
    if (kind === 'tables') {
      const { data } = await api.get<TableInfo[]>('/tables/mine')
      myTables.value = Array.isArray(data) ? data : []
    } else if (kind === 'friends') {
      await loadFriends()
      startFriendPresenceRefresh()
    } else if (kind === 'logo') {
      const { data } = await api.get<{ groups: LogoGroup[]; selected_token_id?: string | null }>('/settings/logo-options')
      logoGroups.value = data.groups || []
      selectedLogo.value = data.selected_token_id || null
	} else if (kind === 'account') {
		leagueFirstName.value = auth.user?.online_league_first_name || ''
		leagueLastName.value = auth.user?.online_league_last_name || ''
    }
  } catch (error: any) {
    message.value = error?.response?.data?.error || 'Could not load this view.'
  } finally {
    loading.value = false
  }
}

async function onOpenMyTables(event: Event): Promise<void> {
  if (!auth.isAuthenticated) return
  editingTable.value = null
  const showLimit = (event as CustomEvent<{ tableLimitReached?: boolean }>).detail?.tableLimitReached === true
  const pending = openPopup('tables')
  tableLimitNotice.value = showLimit
  await pending
}

function openArmies(): void {
  const resolved = router.resolve('/armies')
  window.open(resolved.href, '_blank', 'noopener,noreferrer')
  menuOpen.value = false
}

async function requestNicknameChange(): Promise<void> {
  try {
    const { data } = await api.post('/auth/request-nickname-change')
    message.value = data.message || 'Check your email.'
  } catch (error: any) {
    message.value = error?.response?.data?.error || 'Could not send the nickname link.'
  }
}

async function requestPasswordChange(): Promise<void> {
  try {
    const { data } = await api.post('/auth/forgot-password', { email: auth.user?.email })
    message.value = data.message || 'Check your email.'
  } catch (error: any) {
    message.value = error?.response?.data?.error || 'Could not send the password link.'
  }
}

async function saveOnlineLeagueData(): Promise<void> {
	if (savingLeagueData.value) return
	savingLeagueData.value = true
	message.value = ''
	try {
		const { data } = await api.put('/settings/online-league', {
			first_name: leagueFirstName.value,
			last_name: leagueLastName.value,
		})
		if (auth.user) {
			auth.user.online_league_name = data.online_league_name || ''
			auth.user.online_league_first_name = data.online_league_first_name || ''
			auth.user.online_league_last_name = data.online_league_last_name || ''
		}
		message.value = 'Online League data saved.'
	} catch (error: any) {
		message.value = error?.response?.data?.error || 'Could not save Online League data.'
	} finally {
		savingLeagueData.value = false
	}
}

async function chooseLogo(token: LogoToken): Promise<void> {
  try {
    const { data } = await api.put('/settings/logo', { token_id: token.id })
    selectedLogo.value = token.id
    if (auth.user) {
      auth.user.logo_token_id = token.id
      auth.user.logo_url = data.logo_url
      auth.user.logo_rect = data.logo_rect || token.rect || null
      auth.user.logo_kind = data.logo_kind || token.kind || 'u'
    }
  } catch (error: any) {
    message.value = error?.response?.data?.error || 'Could not save this logo.'
  }
}

async function removeFriend(friend: FriendItem): Promise<void> {
  try {
    await lobby.removeFriend(friend.id)
    friends.value = friends.value.filter(item => item.id !== friend.id)
  } catch (error: any) {
    message.value = error?.response?.data?.error || 'Could not remove friend.'
  }
}

async function logout(): Promise<void> {
  menuOpen.value = false
  await lobby.leaveAuthenticatedPresence()
  await auth.logout()
  await router.push('/')
}

function openTable(table: TableInfo): void {
  editingTable.value = table
}

function visitTable(tableId: string): void {
  const resolved = router.resolve({ name: 'play', params: { id: tableId } })
  window.open(resolved.href, '_blank', 'noopener,noreferrer')
}

async function reloadTables(): Promise<void> {
  const { data } = await api.get<TableInfo[]>('/tables/mine')
  myTables.value = Array.isArray(data) ? data : []
  editingTable.value = null
}

async function deleteOwnTable(table: TableInfo): Promise<void> {
  if (!window.confirm(`Delete table ${table.name}?`)) return
  try {
    await lobby.deleteTable(table.id)
    myTables.value = myTables.value.filter(item => item.id !== table.id)
  } catch (error: any) {
    message.value = error?.response?.data?.error || 'Could not delete table.'
  }
}

async function refreshOpenSocialPopup(): Promise<void> {
	if (popup.value !== 'friends') return
	try {
		await loadFriends()
	} catch {
		// Keep the current rows visible while a realtime refresh is retried.
	}
}

onMounted(() => {
  window.addEventListener(MY_TABLES_EVENT, onOpenMyTables)
	document.addEventListener('pointerdown', onDocumentPointerDown, true)
	window.addEventListener('nhex:social-changed', refreshOpenSocialPopup)
})
onUnmounted(() => {
  window.removeEventListener(MY_TABLES_EVENT, onOpenMyTables)
	stopFriendPresenceRefresh()
	document.removeEventListener('pointerdown', onDocumentPointerDown, true)
	window.removeEventListener('nhex:social-changed', refreshOpenSocialPopup)
})
</script>

<template>
  <nav class="nhex-navbar menu">
    <div class="navbar-left">
      <RouterLink to="/" class="brand-link"><span class="nhex1" data-n="Nhex">Nhex</span></RouterLink>
      <div id="nav-breadcrumbs" class="navBtn navbar-breadcrumbs"><slot /></div>
    </div>

    <div ref="rootRef" class="profile-shell">
      <template v-if="auth.isAuthenticated">
        <button class="profile-pill" type="button" :aria-expanded="menuOpen" :style="{ '--profile-role-color': roleColor(auth.user?.role) }" @click="menuOpen = !menuOpen">
          <span class="profile-logo-wrap" title="Change Logo" @click.stop="openPopup('logo')">
            <ArmyTokenPreview :path="profileLogo" :kind="profileLogoKind" :rect="profileLogoRect" />
            <span class="profile-pencil" aria-hidden="true">&#9998;</span>
          </span>
          <span class="profile-name">{{ auth.username }}</span>
        </button>

        <div v-if="menuOpen" class="profile-menu" @click.stop>
          <button type="button" @click="openPopup('tables')">My Tables</button>
          <button type="button" @click="openArmies">My Armies <span aria-hidden="true">&#8599;</span></button>
          <button type="button" @click="openPopup('friends')">Friends</button>
          <button type="button" @click="openPopup('account')">Change nick &amp; pass</button>
          <button type="button" class="logout" @click="logout">Logout</button>
        </div>
      </template>
      <div v-else class="guest-nav-actions">
        <RouterLink :to="{ name: 'lobby', query: { auth: 'login' } }">Login</RouterLink>
        <RouterLink :to="{ name: 'lobby', query: { auth: 'register' } }" class="signup">Register</RouterLink>
      </div>
    </div>
  </nav>

  <Teleport to="body">
    <div v-if="popup || editingTable" class="profile-overlay" @pointerdown.self="closeAll">
      <TableFormModal
        v-if="editingTable"
        embedded
        show-back
        :visible="true"
        mode="edit"
        :table-data="editingTable"
        @close="closeAll"
        @back="editingTable = null"
        @saved="reloadTables"
        @deleted="reloadTables"
        @open="visitTable"
      />
      <section
        v-else-if="popup"
        class="profile-popup nhex-popup-surface"
        :class="[`nhex-popup--profile-${popup}`, { 'nhex-popup-scrollbar': popup === 'logo' }]"
        @pointerdown.stop
      >
        <img class="popup-art" src="/media/site/Posterunek.avif" alt="" draggable="false" />
        <button type="button" class="profile-close" aria-label="Close" @click="closeAll">&times;</button>

        <template v-if="popup === 'tables'">
          <h2>My Tables</h2>
          <p v-if="tableLimitNotice && atTableLimit" class="table-limit-notice" role="alert" style="color: #af0d0d">Delete an existing table below before creating a new one.</p>
          <p class="popup-summary" :class="{ limit: atTableLimit }">{{ myTables.length }}/{{ auth.isAdmin ? 'unlimited' : 2 }} tables</p>
          <div v-if="myTables.length" class="popup-list">
            <div v-for="table in myTables" :key="table.id" class="my-table-row">
              <button type="button" class="my-table-edit" @click="openTable(table)"><span>{{ table.name }}</span></button>
              <button type="button" class="my-table-delete" title="Delete table" :aria-label="`Delete ${table.name}`" @click="deleteOwnTable(table)">&times;</button>
            </div>
          </div>
          <p v-else-if="!loading" class="empty-state">No tables created yet.</p>
        </template>

        <template v-else-if="popup === 'friends'">
          <h2>Friends</h2>
          <p class="popup-summary">{{ friends.length }} {{ friends.length === 1 ? 'friend' : 'friends' }}</p>
          <div v-if="friends.length" class="popup-list">
            <div v-for="friend in friends" :key="friend.id" class="friend-row">
              <span class="status-dot" :class="friendPresence(friend).tone" :title="friendPresence(friend).label" />
              <button type="button" class="friend-name" :style="{ color: roleColor(friend.role) }" :title="friend.username" @click="selectedFriend = friend">{{ friend.username }}</button>
              <button type="button" class="friend-remove" title="Remove friend" :aria-label="`Remove ${friend.username} from friends`" @click="removeFriend(friend)">&times;</button>
            </div>
          </div>
          <p v-else-if="!loading" class="empty-state">No friends yet.</p>
        </template>

        <template v-else-if="popup === 'account'">
          <h2 class="modal-title">Change nick &amp; pass</h2>
          <p class="popup-summary">Nickname can be changed once every 30 days. Links are sent to your verified email.</p>
          <div class="account-actions">
            <button type="button" class="modal-submit-btn" @click="requestNicknameChange">Change nickname</button>
            <button type="button" class="modal-submit-btn" @click="requestPasswordChange">Change password</button>
          </div>
		  <form class="league-profile-form" @submit.prevent="saveOnlineLeagueData">
			<fieldset>
			  <legend>Online League data <small>optional</small></legend>
			  <div class="league-name-fields">
				<label>First name<input v-model="leagueFirstName" class="nhex-popup-input profile-input" type="text" maxlength="80" autocomplete="given-name" /></label>
				<label>Last name<input v-model="leagueLastName" class="nhex-popup-input profile-input" type="text" maxlength="80" autocomplete="family-name" /></label>
			  </div>
			</fieldset>
			<button type="submit" class="modal-submit-btn" :disabled="savingLeagueData">{{ savingLeagueData ? 'Saving...' : 'Save Online League data' }}</button>
		  </form>
        </template>

        <template v-else-if="popup === 'logo'">
          <h2 class="modal-title">Change Logo</h2>
          <p class="popup-summary">Choose a unit from an available army.</p>
          <div class="logo-catalog">
            <section v-for="group in logoGroups" :key="group.id">
              <h3>{{ group.name }}</h3>
              <div class="logo-grid">
                <button v-for="token in group.tokens" :key="token.id" type="button" :class="{ selected: token.id === selectedLogo }" :title="token.name" @click="chooseLogo(token)">
                  <ArmyTokenPreview :path="token.image_url" :kind="token.kind || 'u'" :rect="token.rect" />
                </button>
              </div>
            </section>
          </div>
        </template>

        <p v-if="loading" class="empty-state">Loading...</p>
        <p v-if="message" class="popup-message">{{ message }}</p>
      </section>
    </div>
  </Teleport>

  <PlayerProfileModal
    v-if="selectedFriend"
    :visible="true"
    :username="selectedFriend.username"
    :user-id="selectedFriend.id"
    @close="selectedFriend = null"
  />
</template>

<style scoped>
.nhex-navbar { position: relative; z-index: 1500; width: 100%; min-height: 60px; padding: 4px 10px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.navbar-left { min-width: 0; display: flex; align-items: center; gap: 10px; white-space: nowrap; }
.brand-link { padding: 0 8px; text-decoration: none; }
.navbar-breadcrumbs { min-width: 0; display: inline-flex; align-items: center; }
.profile-shell { position: relative; flex: 0 0 auto; }
.profile-pill { position:relative; isolation:isolate; width:clamp(218px,15vw,364px); min-height:68px; padding:6px 72px; display:grid; place-items:center; border:0; border-radius:10px; clip-path:polygon(16px 0,calc(100% - 16px) 0,100% 50%,calc(100% - 16px) 100%,16px 100%,0 50%); background:transparent; box-shadow:0 0 12px color-mix(in srgb,var(--profile-role-color,#eef2f6) 30%,transparent),0 7px 14px rgba(0,0,0,.42); color:#eef2f6; cursor:pointer; translate:0 0; transition:filter .15s ease,translate .15s ease,box-shadow .15s ease; }
.profile-pill::before,.profile-pill::after { content:''; position:absolute; pointer-events:none; }
.profile-pill::before { z-index:-2; inset:0; clip-path:inherit; background:linear-gradient(135deg,color-mix(in srgb,var(--profile-role-color,#eef2f6) 25%,#fff) 0%,var(--profile-role-color,#eef2f6) 46%,color-mix(in srgb,var(--profile-role-color,#eef2f6) 42%,#070a0d) 100%); }
.profile-pill::after { z-index:-1; inset:2px; clip-path:polygon(15px 0,calc(100% - 15px) 0,100% 50%,calc(100% - 15px) 100%,15px 100%,0 50%); background:linear-gradient(180deg,rgba(38,49,59,.96) 0%,rgba(17,23,29,.98) 48%,rgba(7,10,14,.99) 100%); box-shadow:inset 0 1px 0 rgba(255,255,255,.12),inset 0 -5px 10px rgba(0,0,0,.38); }
.profile-pill:hover { filter:brightness(1.1); translate:0 -1px; box-shadow:inset 0 1px 0 rgba(255,255,255,.16),inset 0 -5px 10px rgba(0,0,0,.34),-5px 0 14px color-mix(in srgb,var(--profile-role-color,#eef2f6) 48%,transparent),5px 0 14px color-mix(in srgb,var(--profile-role-color,#eef2f6) 48%,transparent),0 9px 18px rgba(0,0,0,.5); }
.profile-logo-wrap { position:absolute; left:12px; top:50%; translate:0 -50%; width:62px; height:55px; display:grid; place-items:center; overflow:hidden; clip-path:polygon(25% 1%,75% 1%,100% 50%,75% 99%,25% 99%,0 50%); background:transparent; }
.profile-logo-wrap :deep(.token-svg) { width: 100%; height: 100%; }
.profile-pencil { position: absolute; inset: 0; display: grid; place-items: center; background: rgba(23,51,71,.7); color: #fff; font-size: 17px; opacity: 0; transition: opacity .14s ease; }
.profile-logo-wrap:hover .profile-pencil { opacity: 1; }
.profile-name { max-width:100%; justify-self:center; overflow:hidden; text-overflow:ellipsis; color:var(--profile-role-color,#eef2f6); text-align:center; font:700 17px Inconsolata,monospace; }
.profile-menu { position:absolute; top:calc(100% + 7px); right:0; width:100%; padding:6px; border:1px solid rgba(72,156,214,.28); border-radius:7px; background:linear-gradient(180deg,rgba(18,25,31,.99),rgba(7,10,14,.99)); box-shadow:inset 0 1px 0 rgba(255,255,255,.06),0 16px 40px rgba(0,0,0,.52); transform-origin:top right; }
.profile-menu button { width: 100%; min-height: 34px; padding: 0 11px; border: 0; border-radius: 4px; background: transparent; color: #d6dce2; text-align: left; font: 700 13px Inconsolata, monospace; cursor: pointer; }
.profile-menu button:hover { background: rgba(72,156,214,.15); color: #fff; }
.profile-menu .logout { margin-top: 4px; border-top: 1px solid rgba(255,255,255,.1); color: #ef8e8e; }
.guest-nav-actions { display: flex; align-items: center; gap: 6px; }
.guest-nav-actions a { min-height: 36px; padding: 0 14px; display: inline-flex; align-items: center; border: 1px solid #426f91; border-radius: 4px; background:#182c3b; color:#b8def5; font:700 13px Inconsolata,monospace; text-decoration:none; }
.guest-nav-actions a:hover { border-color:#6ca8d1; background:#23445c; color:#fff; }
.guest-nav-actions .signup { border-color:#5686a8; background:#275574; }

.profile-overlay { position: fixed; inset: 0; z-index: 11000; padding: 18px; display: grid; place-items: center; background: rgba(0,0,0,.68); }
.profile-popup { position: relative; isolation: isolate; width: min(720px, 100%); max-height: min(760px, calc(100vh - 36px)); padding: 24px; overflow: hidden auto; border: 1px solid rgba(255,255,255,.16); border-radius: 8px; background: #090c10; box-shadow: 0 22px 70px rgba(0,0,0,.65); color: #d9dfe5; }
.popup-art { position: absolute; z-index: -1; right: -10%; bottom: 0; width: 58%; max-height: 88%; object-fit: contain; object-position: right bottom; opacity: .2; pointer-events: none; }
.profile-close { position:absolute; top:8px; right:10px; }
.profile-popup h2 { margin: 0 40px 4px 0; color: #f0f3f6; font: 700 21px Inconsolata, monospace; }
.popup-summary { margin: 0 0 16px; color: #8f9aa5; font: 12px/1.45 Inconsolata, monospace; }
.popup-summary.limit { color: #ef4444; }
.popup-list { display: grid; gap: 6px; }
.popup-list > button, .friend-row, .my-table-row { min-height: 40px; padding: 0 11px; display: flex; align-items: center; gap: 9px; border: 1px solid rgba(255,255,255,.1); border-radius: 5px; background: rgba(16,21,27,.9); color: #dce2e8; font: 700 13px Inconsolata, monospace; }
.popup-list > button { justify-content: space-between; cursor: pointer; }
.popup-list > button:hover { border-color: #489cd6; background: #142431; }
.my-table-row { padding-right:5px; }
.my-table-edit { min-width:0; min-height:38px; flex:1; padding:0 6px; display:flex; align-items:center; justify-content:space-between; gap:8px; border:0; background:transparent; color:inherit; font:inherit; cursor:pointer; }
.my-table-edit:hover { color:#9bd2f3; }
.my-table-edit span { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.my-table-delete { width:28px; height:28px; padding:0; border:0; background:transparent; color:#dc7272; font:700 20px/1 Inconsolata,monospace; cursor:pointer; }
.my-table-delete:hover { color:#ff5252; }
.popup-list small { color: #7891a4; }
.friend-name { flex: 1; min-width: 0; padding: 8px 0; border: 0; background: transparent; font: inherit; text-align: left; overflow-wrap: anywhere; cursor: pointer; }
.friend-name:hover { text-decoration: underline; }
.friend-remove { flex: 0 0 auto; border: 0; background: transparent; color: #dc7272; font-size: 20px; cursor: pointer; }
.status-dot { width: 7px; height: 7px; flex:0 0 auto; border-radius: 50%; background:#b33434; box-shadow:0 0 6px rgba(179,52,52,.45); }
.status-dot.available, .status-dot.playing { background:#32d47d; box-shadow:0 0 6px rgba(50,212,125,.45); }
.status-dot.unavailable { background:#d9822b; box-shadow:0 0 6px rgba(217,130,43,.45); }
.empty-state { color: #78838e; font: 13px Inconsolata, monospace; }
.account-actions { display: grid; gap: 9px; width: min(330px, 100%); margin: 24px auto 8px; }
.account-actions button { min-height: 42px; }
.popup-message { margin: 14px 0 0; padding: 8px 10px; border: 1px solid rgba(72,156,214,.25); border-radius: 4px; background: rgba(72,156,214,.09); color: #b9d8ed; font: 12px Inconsolata, monospace; }
.logo-catalog { padding:12px; display:grid; align-content:start; gap:16px; border-radius:6px; }
.logo-catalog h3 { margin: 0 0 7px; color: #9da8b2; font: 700 14px Inconsolata, monospace; }
.logo-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(72px, 1fr)); gap: 7px; }
.logo-grid button { aspect-ratio: 1; padding: 5px; border: 2px solid rgba(255,255,255,.1); border-radius: 5px; background: rgba(255,255,255,.035); cursor: pointer; }
.logo-grid button:hover { border-color: #489cd6; }
.logo-grid button.selected { border-color: #22c55e; box-shadow: 0 0 0 2px rgba(34,197,94,.18); }
.logo-grid :deep(.token-svg) { width: 100%; height: 100%; }
.league-profile-form { width:min(470px,100%); margin:20px auto 0; display:grid; gap:10px; }
.league-profile-form fieldset { min-width:0; margin:0; padding:10px 12px 12px; border:1px solid rgba(255,255,255,.13); border-radius:5px; }
.league-profile-form legend { padding:0 5px; color:#c7d0d8; font:700 12px Inconsolata,monospace; }
.league-profile-form legend small { color:#76828d; font-weight:400; }
.league-name-fields { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:9px; }
.league-name-fields label { min-width:0; display:grid; gap:4px; color:#8f9aa5; font:11px Inconsolata,monospace; }
.profile-input { width:100%; min-width:0; height:45px; box-sizing:border-box; padding:0 11px; border:0; border-radius:5px; background:rgba(18,24,30,.82); color:#edf3f7; box-shadow:inset 0 0 0 1px rgba(255,255,255,.1); font:13px Inconsolata,monospace; transition:box-shadow .14s ease,background-color .14s ease; }
.profile-input:focus { outline:0; background:rgba(21,30,38,.94); box-shadow:inset 0 0 0 1px #489cd6,0 0 0 2px rgba(72,156,214,.12); }
.league-profile-form > button { min-height:38px; }
.league-profile-form > button:disabled { opacity:.55; cursor:wait; }
@media (max-width:520px) { .league-name-fields { grid-template-columns:1fr; } }
@media (max-width: 680px) { .profile-pill{width:clamp(218px,48vw,280px);min-height:62px;padding:6px 62px}.profile-logo-wrap{width:54px;height:48px}.profile-name { max-width: 100%; font-size:15px; } .navbar-breadcrumbs { max-width: 42vw; overflow: hidden; } .profile-overlay { padding: 8px; } .profile-popup { max-height: calc(100vh - 16px); padding: 20px 16px; } }
</style>
